import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../components/common/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import {
  APPLICATION_OPTIONS,
  createRole,
  INTRANET_APPLICATION_ID,
  INTRANET_MOBILE_APPLICATION_ID,
  listApplications,
  listMetaCodes,
  updateRole,
  viewOrderIndexes,
  viewPermissionDetails,
  viewRoleDetails,
  type ApplicationOption,
  type OrderIndexRow,
  type PermissionGroup,
} from '../../services/roleAdministration';
import { setFlashToast } from '../../utils/flashToast';
import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../utils/requiredValidation';
import { suggestUniqueRoleMetaCode } from '../../utils/roleCodeGeneration';
import { ROLE_DESCRIPTION_MAX_LEN, ROLE_NAME_MAX_LEN } from '../../utils/roleFieldValidation';
import { SystemInfoBox, SystemInformationSection } from '../../components/ui/SystemInformation';

type OrderRowKey = string;

const EMPTY_VALUE = '\u2014';

function toDisplayDate(v?: string) {
  if (!v) return EMPTY_VALUE;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
}

const CODE_DISABLED_STYLE = {
  background: 'color-mix(in srgb, var(--bg) 70%, var(--surface))',
  color: 'var(--t2)',
  cursor: 'not-allowed' as const,
};

const READONLY_FIELD_STYLE = CODE_DISABLED_STYLE;

export default function RoleFormPage() {
  const { t } = useTranslation();
  const { roleId } = useParams();
  const numericRoleId = roleId ? Number(roleId) : null;
  const isView = Boolean(useMatch('/admin/roles-permissions/:roleId/view'));
  const isEdit = numericRoleId != null && Number.isFinite(numericRoleId);
  const readOnly = isView;
  const nav = useNavigate();
  const location = useLocation();

  const [apps, setApps] = useState<ApplicationOption[]>(APPLICATION_OPTIONS);
  const [allowedOnIntranet, setAllowedOnIntranet] = useState(true);
  const [allowedOnIntranetMobile, setAllowedOnIntranetMobile] = useState(false);

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [active, setActive] = useState(true);
  const [assignedUserCount, setAssignedUserCount] = useState(0);
  const [description, setDescription] = useState('');

  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [permissionIds, setPermissionIds] = useState<number[]>([]);
  const [openGroup, setOpenGroup] = useState<number | null>(null);

  const [orderRowsApi, setOrderRowsApi] = useState<OrderIndexRow[]>([]);
  const [orderIds, setOrderIds] = useState<OrderRowKey[]>([]);
  const currentOrderKey: OrderRowKey = isEdit ? String(numericRoleId as number) : 'new';
  const [orderDirty, setOrderDirty] = useState(false);
  const [dragId, setDragId] = useState<OrderRowKey | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [codeResolving, setCodeResolving] = useState(false);
  const [error, setError] = useState('');
  const [reqErrors, setReqErrors] = useState<RequiredErrors>({});
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);

  const savingRef = useRef(false);
  const metaCodesRef = useRef<string[]>([]);

  const [createdAt, setCreatedAt] = useState<string>(EMPTY_VALUE);
  const [createdBy, setCreatedBy] = useState<string>(EMPTY_VALUE);
  const [updatedAt, setUpdatedAt] = useState<string>(EMPTY_VALUE);
  const [updatedBy, setUpdatedBy] = useState<string>(EMPTY_VALUE);
  const [roleDetailsLoaded, setRoleDetailsLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const [appRes, codesRes] = await Promise.all([listApplications(), listMetaCodes()]);
      if (!alive) return;
      if (appRes.success && appRes.data.length) {
        setApps(appRes.data);
      }
      if (codesRes.success) metaCodesRef.current = codesRes.data;
    })();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    if (isEdit) {
      setLoading(true);
      setError('');
      setRoleDetailsLoaded(false);
    }

    const load = async () => {
      if (isEdit && numericRoleId != null) {
        const res = await viewRoleDetails(numericRoleId);
        if (!alive) return;
        if (!res.success) {
          setError(res.message);
          setLoading(false);
          return;
        }
        setName(res.data.roleName);
        setCode((res.data.metaCode ?? '').toUpperCase());
        setActive(res.data.active);
        setAssignedUserCount(res.data.assignedUserCount ?? 0);
        setDescription(res.data.description ?? '');
        setAllowedOnIntranet(res.data.allowedOnIntranet ?? true);
        setAllowedOnIntranetMobile(res.data.allowedOnIntranetMobile ?? false);
        setCreatedAt(toDisplayDate(res.data.createdAt));
        setCreatedBy(res.data.createdByName ?? EMPTY_VALUE);
        setUpdatedAt(toDisplayDate(res.data.updatedAt));
        setUpdatedBy(res.data.updatedByName ?? EMPTY_VALUE);
        setRoleDetailsLoaded(true);
      }

      const order = await viewOrderIndexes({ inputMode: isEdit ? 'UPDATE' : 'CREATE', roleId: isEdit ? (numericRoleId as number) : undefined });
      if (!alive) return;
      if (order.success) {
        setOrderRowsApi(order.data);
        setOrderDirty(false);
      } else {
        setError(order.message);
      }
    };

    void load();
    return () => {
      alive = false;
    };
  }, [isEdit, numericRoleId]);

  useEffect(() => {
    if (isEdit && !roleDetailsLoaded) return;
    if (!allowedOnIntranet && !allowedOnIntranetMobile) {
      setGroups([]);
      if (!isEdit) setPermissionIds([]);
      setLoading(false);
      return;
    }

    let alive = true;
    setLoading(true);
    setError('');

    const load = async () => {
      const perms = await viewPermissionDetails({
        allowedOnIntranet,
        allowedOnIntranetMobile,
        roleId: isEdit && numericRoleId != null ? numericRoleId : undefined,
        inputMode: isEdit ? 'UPDATE' : 'CREATE',
      });
      if (!alive) return;
      if (!perms.success) {
        setError(perms.message);
        setLoading(false);
        return;
      }
      setGroups(perms.data);
      if (isEdit) {
        const selected = perms.data.flatMap((g) => g.permissions.filter((p) => p.selected).map((p) => p.permissionId));
        setPermissionIds(selected);
      } else {
        setPermissionIds([]);
      }
      setOpenGroup(perms.data[0]?.groupId ?? null);
      setLoading(false);
    };

    void load();
    return () => {
      alive = false;
    };
  }, [allowedOnIntranet, allowedOnIntranetMobile, isEdit, numericRoleId, roleDetailsLoaded]);

  useEffect(() => {
    if (isEdit) return;
    const trimmed = name.trim();
    if (!trimmed) {
      setCode('');
      return;
    }

    let alive = true;
    setCodeResolving(true);
    const timer = window.setTimeout(() => {
      void (async () => {
        let codes = metaCodesRef.current;
        if (!codes.length) {
          const res = await listMetaCodes();
          if (!alive) return;
          if (res.success) {
            codes = res.data;
            metaCodesRef.current = codes;
          }
        }
        if (!alive) return;
        setCode(suggestUniqueRoleMetaCode(trimmed, codes));
        setCodeResolving(false);
      })();
    }, 280);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [name, isEdit]);

  useEffect(() => {
    // Initialize the order list strictly from API response.
    // Once the user drags, we preserve their local ordering.
    if (orderDirty) return;

    const toKey = (r: OrderIndexRow): OrderRowKey => (r.roleId == null ? 'new' : String(r.roleId));
    const ids = [...orderRowsApi].sort((a, b) => a.orderIndex - b.orderIndex).map(toKey);
    const selectedKeyFromApi = (() => {
      const sel = orderRowsApi.find((r) => r.selected);
      return sel ? toKey(sel) : null;
    })();
    const currentKey = selectedKeyFromApi ?? currentOrderKey;

    if (ids.length === 0) {
      setOrderIds([currentKey]);
      return;
    }

    setOrderIds(ids);
  }, [orderRowsApi, currentOrderKey, orderDirty]);

  const selectedOrderKey = useMemo<OrderRowKey | null>(() => {
    const sel = orderRowsApi.find((r) => r.selected);
    if (!sel) return null;
    return sel.roleId == null ? 'new' : String(sel.roleId);
  }, [orderRowsApi]);

  const orderRows = useMemo(() => {
    const byId = new Map<OrderRowKey, string>(
      orderRowsApi.map((r) => [(r.roleId == null ? 'new' : String(r.roleId)) as OrderRowKey, r.roleName])
    );
    const currentId = selectedOrderKey ?? currentOrderKey;
    return orderIds.map((id, idx) => ({
      id,
      idx: idx + 1,
      name: byId.get(id) || (id === 'new' && !isEdit ? t('admin.shared.newRole') : EMPTY_VALUE),
      isCurrent: id === currentId,
    }));
  }, [orderIds, orderRowsApi, currentOrderKey, selectedOrderKey, isEdit, t]);

  const selectedCount = permissionIds.length;
  const allPermissionIds = useMemo(
    () => Array.from(new Set(groups.flatMap((g) => g.permissions.map((p) => p.permissionId)))),
    [groups]
  );
  const allSelected = allPermissionIds.length > 0 && allPermissionIds.every((id) => permissionIds.includes(id));

  const requiredSpecs: RequiredFieldSpec[] = useMemo(
    () => [
      { key: 'name', label: t('admin.shared.roleName'), value: name, elementId: 'role-name' },
      { key: 'code', label: t('admin.shared.code'), value: code, elementId: 'role-code' },
      {
        key: 'applications',
        label: t('admin.shared.applicationNames'),
        value: allowedOnIntranet || allowedOnIntranetMobile ? 'selected' : '',
        elementId: 'role-applications',
      },
      { key: 'permissionIds', label: t('admin.shared.rolePermissions'), value: permissionIds, elementId: 'role-permissions' },
    ],
    [name, code, allowedOnIntranet, allowedOnIntranetMobile, permissionIds, t]
  );

  function clearReqError(key: string) {
    setReqErrors((cur) => {
      if (!cur[key]) return cur;
      const next = { ...cur };
      delete next[key];
      return next;
    });
  }

  function isApplicationChecked(appId: number): boolean {
    if (appId === INTRANET_APPLICATION_ID) return allowedOnIntranet;
    if (appId === INTRANET_MOBILE_APPLICATION_ID) return allowedOnIntranetMobile;
    return false;
  }

  function toggleApplication(appId: number, checked: boolean) {
    if (readOnly) return;
    if (appId === INTRANET_APPLICATION_ID) setAllowedOnIntranet(checked);
    else if (appId === INTRANET_MOBILE_APPLICATION_ID) setAllowedOnIntranetMobile(checked);
    else return;
    clearReqError('applications');
    if (!isEdit) setPermissionIds([]);
    setOrderDirty(false);
  }

  async function handleSave() {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);

    let keepLocked = false;
    try {
      setError('');
      const nm = name.trim();
      const cd = code.trim();
      const errs = validateRequired(requiredSpecs);
      const merged: RequiredErrors = { ...errs };
      const maxExceeded = t('profile.maxCharLimit');

      if (!permissionIds.length) merged.permissionIds = t('admin.roleForm.atLeastOnePermission');
      if (!allowedOnIntranet && !allowedOnIntranetMobile) {
        merged.applications = t('admin.roleForm.atLeastOneApplication');
      }
      if (nm.length > ROLE_NAME_MAX_LEN) merged.name = maxExceeded;
      if (description.trim().length > ROLE_DESCRIPTION_MAX_LEN) merged.description = maxExceeded;
      if (!cd && !isEdit) merged.code = t('admin.roleForm.codeGenerateFailed');

      if (Object.keys(merged).length) {
        setReqErrors(merged);
        focusFirstInvalid(requiredSpecs, merged);
        return;
      }

      const currentId = selectedOrderKey ?? currentOrderKey;
      const idx = orderIds.indexOf(currentId);
      const orderIndex = idx >= 0 ? idx + 1 : 1;

      if (isEdit && numericRoleId != null) {
        const res = await updateRole({
          roleId: numericRoleId,
          code: cd,
          allowedOnIntranet,
          allowedOnIntranetMobile,
          name: nm,
          description: description.trim(),
          active,
          orderIndex,
          permissionIds,
        });
        if (!res.success) {
          nav(location.pathname, { replace: true, state: { toast: { message: res.message ?? t('admin.shared.requestFailed'), variant: 'error' } } });
          return;
        }
        setFlashToast({ message: t('admin.roleForm.updateSuccess'), variant: 'success' });
        nav('/admin/roles-permissions');
        keepLocked = true;
      } else {
        const res = await createRole({
          allowedOnIntranet,
          allowedOnIntranetMobile,
          name: nm,
          description: description.trim(),
          code: cd,
          active: true,
          orderIndex,
          permissionIds,
        });
        if (!res.success) {
          nav(location.pathname, { replace: true, state: { toast: { message: res.message ?? t('admin.shared.requestFailed'), variant: 'error' } } });
          return;
        }
        setFlashToast({ message: t('admin.roleForm.createSuccess'), variant: 'success' });
        nav('/admin/roles-permissions');
        keepLocked = true;
      }
    } finally {
      if (!keepLocked) {
        savingRef.current = false;
        setSaving(false);
      }
    }
  }

  const pageTitle = isView
    ? t('admin.roleForm.viewTitle')
    : isEdit
      ? t('admin.roleForm.updateTitle')
      : t('admin.roleForm.addTitle');
  const bcLeaf = pageTitle;
  const activeLockedByAssignedUsers = isEdit && active && assignedUserCount > 0;
  const activeCheckboxDisabled = readOnly || activeLockedByAssignedUsers;
  const activeCheckboxTooltip = activeLockedByAssignedUsers
    ? t('admin.roleForm.cannotInactivateWithAssignedUsers')
    : undefined;

  return (
    <>
      <div className="bc">
        <span>{t('admin.userAdministration')}</span>
        <span className="bc-sep">/</span>
        <Link to="/admin/roles-permissions">{t('admin.roleForm.listTitle')}</Link>
        <span className="bc-sep">/</span>
        {bcLeaf}
      </div>

      <div className="pg-title">{pageTitle}</div>

      <div className="cr-table-wrap" style={{ overflow: 'hidden' }}>
        <div style={{ padding: 18 }}>
          {loading ? <div style={{ color: 'var(--t2)', marginBottom: 10 }}>{t('common.loading')}</div> : null}
          {error ? <div style={{ color: 'var(--danger)', marginBottom: 10 }}>{error}</div> : null}

          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--t2)', marginBottom: 10 }}>{t('admin.shared.basicInformation')}</div>

          <div style={{ display: 'grid', gap: 12 }}>
            <div className="al-f">
              <div className="al-l">
                {t('admin.shared.roleName')} <span className="req">*</span>
              </div>
              <input
                id="role-name"
                className="al-inp"
                value={name}
                placeholder={t('admin.roleForm.roleNamePlaceholder')}
                maxLength={ROLE_NAME_MAX_LEN}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setName(e.target.value);
                  if (e.target.value.trim()) clearReqError('name');
                }}
                style={{
                  ...(reqErrors.name ? { borderColor: 'var(--red)' } : {}),
                  ...(readOnly ? READONLY_FIELD_STYLE : {}),
                }}
                aria-invalid={reqErrors.name ? true : undefined}
                aria-describedby={reqErrors.name ? 'role-name-err' : undefined}
              />
              {reqErrors.name ? (
                <div id="role-name-err" style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>
                  {reqErrors.name}
                </div>
              ) : null}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'start' }}>
              <div className="al-f">
                <div className="al-l">
                  {t('admin.shared.code')} <span className="req">*</span>
                </div>
                <input
                  id="role-code"
                  className="al-inp"
                  value={codeResolving && !code ? '\u2026' : code}
                  placeholder={t('admin.roleForm.codePlaceholder')}
                  readOnly
                  disabled
                  style={{
                    ...(reqErrors.code ? { borderColor: 'var(--red)' } : {}),
                    ...CODE_DISABLED_STYLE,
                  }}
                  aria-invalid={reqErrors.code ? true : undefined}
                  aria-describedby={reqErrors.code ? 'role-code-err' : undefined}
                />
                {reqErrors.code ? (
                  <div id="role-code-err" style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>
                    {reqErrors.code}
                  </div>
                ) : null}
              </div>
              {isEdit ? (
                <div className="al-f">
                  <div className="al-l" aria-hidden="true" style={{ visibility: 'hidden' }}>
                    {t('common.active')}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', height: 34 }}>
                    <span
                      title={activeCheckboxTooltip}
                      style={{
                        display: 'inline-flex',
                        cursor: activeLockedByAssignedUsers ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <label className="ccr-chk" style={{ marginBottom: 0, alignItems: 'center' }}>
                        <input
                          id="role-active"
                          type="checkbox"
                          checked={active}
                          disabled={activeCheckboxDisabled}
                          onChange={(e) => setActive(e.target.checked)}
                        />
                        <span>{t('common.active')}</span>
                      </label>
                    </span>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="al-f">
              <div className="al-l">{t('admin.shared.description')}</div>
              <textarea
                className="al-inp"
                style={{
                  height: 90,
                  paddingTop: 10,
                  paddingBottom: 10,
                  resize: 'vertical',
                  ...(readOnly ? READONLY_FIELD_STYLE : {}),
                }}
                value={description}
                placeholder={t('admin.roleForm.descriptionPlaceholder')}
                maxLength={ROLE_DESCRIPTION_MAX_LEN}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (e.target.value.trim().length <= ROLE_DESCRIPTION_MAX_LEN) clearReqError('description');
                }}
                aria-invalid={reqErrors.description ? true : undefined}
              />
              {reqErrors.description ? (
                <div id="role-description-err" style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>
                  {reqErrors.description}
                </div>
              ) : null}
            </div>

            <div className="al-f" id="role-applications">
              <div className="al-l">
                {t('admin.shared.applicationNames')} <span className="req">*</span>
              </div>
              <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
                {apps.map((a) => (
                  <label key={a.id} className="ccr-chk" style={{ marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={isApplicationChecked(a.id)}
                      onChange={(e) => toggleApplication(a.id, e.target.checked)}
                      disabled={readOnly}
                    />
                    <span>{a.label}</span>
                  </label>
                ))}
              </div>
              {reqErrors.applications ? (
                <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>
                  {reqErrors.applications}
                </div>
              ) : null}
            </div>
          </div>

          <div style={{ height: 16 }} />

          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--t2)', marginBottom: 8 }}>
            {t('admin.shared.rolePermissions')}{' '}
            <span style={{ color: 'var(--t3)', fontWeight: 700 }}>({t('admin.shared.selectedCount', { count: selectedCount })})</span>
          </div>

          <div
            id="role-permissions"
            style={{
              border: `1px solid ${reqErrors.permissionIds ? 'var(--red)' : 'var(--border)'}`,
              borderRadius: 'var(--r)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '10px 12px',
                background: 'color-mix(in srgb, var(--bg) 55%, var(--surface))',
                fontWeight: 700,
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <input
                type="checkbox"
                checked={allSelected}
                disabled={readOnly}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setPermissionIds(checked ? allPermissionIds : []);
                  if (checked) clearReqError('permissionIds');
                }}
              />
              {t('admin.shared.selectAllPermissions')}
            </div>

            {groups.map((g) => {
              const groupSelected = g.permissions.filter((p) => permissionIds.includes(p.permissionId)).length;
              const isOpen = openGroup === g.groupId;
              return (
                <div key={g.groupId} style={{ borderTop: '1px solid var(--bl)' }}>
                  <button
                    type="button"
                    className="th-inner"
                    style={{ width: '100%', justifyContent: 'space-between', cursor: 'pointer' }}
                    onClick={() => setOpenGroup((cur) => (cur === g.groupId ? null : g.groupId))}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input
                        type="checkbox"
                        checked={g.permissions.length > 0 && groupSelected === g.permissions.length}
                        disabled={readOnly}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setPermissionIds((cur) => {
                            const set = new Set(cur);
                            g.permissions.forEach((p) => {
                              if (checked) set.add(p.permissionId);
                              else set.delete(p.permissionId);
                            });
                            return Array.from(set);
                          });
                          if (checked) clearReqError('permissionIds');
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span style={{ fontWeight: 800, color: 'var(--text)' }}>
                        {g.groupName} <span style={{ color: 'var(--t3)', fontWeight: 700 }}>({groupSelected})</span>
                      </span>
                    </span>
                    <span style={{ color: 'var(--t3)' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <polyline points={isOpen ? '6 15 12 9 18 15' : '6 9 12 15 18 9'} />
                      </svg>
                    </span>
                  </button>

                  {isOpen ? (
                    <div style={{ padding: 12, background: 'var(--surface)' }}>
                      {g.permissions.length === 0 ? (
                        <div style={{ color: 'var(--t3)', fontSize: 12 }}>{t('admin.shared.noPermissionsConfigured')}</div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                          {g.permissions.map((p) => (
                            <label key={p.permissionId} className="ccr-chk" style={{ marginBottom: 0 }}>
                              <input
                                type="checkbox"
                                checked={permissionIds.includes(p.permissionId)}
                                disabled={readOnly}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setPermissionIds((cur) => (checked ? [...cur, p.permissionId] : cur.filter((x) => x !== p.permissionId)));
                                  if (checked) clearReqError('permissionIds');
                                }}
                              />
                              <span>{p.permissionName}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          {reqErrors.permissionIds ? (
            <div id="role-permissions-err" style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }}>
              {reqErrors.permissionIds}
            </div>
          ) : null}

          <div style={{ height: 16 }} />

          <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--t2)', marginBottom: 8 }}>{t('admin.shared.orderIndex')}</div>
          <div className="oi-wrap">
            {orderRows.length === 0 ? (
              <div style={{ padding: 12, color: 'var(--t3)' }}>{t('admin.shared.loadingOrder')}</div>
            ) : null}
            {orderRows.map((x, i) => (
              <div
                key={x.id}
                className={`oi-item ${x.isCurrent ? 'cur' : 'lock'} ${dragId === x.id ? 'dragging' : ''}`}
                draggable={!readOnly && x.isCurrent}
                onDragStart={() => (!readOnly && x.isCurrent ? setDragId(x.id) : null)}
                onDragEnd={() => setDragId(null)}
                onDragOver={(e) => {
                  if (readOnly || !dragId) return;
                  e.preventDefault();
                }}
                onDrop={() => {
                  if (readOnly || !dragId || dragId === x.id) return;
                  setOrderDirty(true);
                  setOrderIds((ids) => {
                    const from = ids.indexOf(dragId);
                    const to = ids.indexOf(x.id);
                    if (from < 0 || to < 0) return ids;
                    const next = [...ids];
                    next.splice(from, 1);
                    next.splice(to, 0, dragId);
                    return next;
                  });
                }}
                style={{ marginTop: i === 0 ? 0 : 8 }}
                title={x.isCurrent ? t('admin.shared.dragToReorder') : t('admin.shared.locked')}
              >
                <div className="oi-left">
                  <div className="oi-num">{x.idx}.</div>
                  {x.isCurrent ? (
                    <div className="oi-grip" aria-hidden="true">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="9" cy="7" r="1.4" />
                        <circle cx="15" cy="7" r="1.4" />
                        <circle cx="9" cy="12" r="1.4" />
                        <circle cx="15" cy="12" r="1.4" />
                        <circle cx="9" cy="17" r="1.4" />
                        <circle cx="15" cy="17" r="1.4" />
                      </svg>
                    </div>
                  ) : null}
                  <div className="oi-name">{x.name}</div>
                </div>

                <div className="oi-right" aria-hidden="true">
                  {x.isCurrent ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14" />
                      <path d="M5 12h14" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{ height: 16 }} />

          {isEdit ? (
            <SystemInformationSection showDivider={false} title={t('profile.systemInformation')}>
              <SystemInfoBox label={t('profile.createdAt')} value={createdAt} />
              <SystemInfoBox label={t('profile.createdBy')} value={createdBy} />
              <SystemInfoBox label={t('profile.lastUpdatedAt')} value={updatedAt} />
              <SystemInfoBox label={t('profile.lastUpdatedBy')} value={updatedBy} />
            </SystemInformationSection>
          ) : null}
        </div>

        <div className="tft" style={{ background: 'color-mix(in srgb, var(--bg) 60%, var(--surface))' }}>
          <div />
          <div className="pgr">
            {readOnly ? (
              <button className="mfb" type="button" onClick={() => nav('/admin/roles-permissions')}>
                {t('common.back')}
              </button>
            ) : (
              <>
                <button className="mfb" type="button" disabled={saving} onClick={() => setCancelConfirmOpen(true)}>
                  {t('common.cancel')}
                </button>
                <Button variant="primary" disabled={saving || loading} onClick={() => void handleSave()}>
                  {saving ? t('common.saving') : t('common.save')}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {!readOnly ? (
        <ConfirmDialog
          isOpen={cancelConfirmOpen}
          title={t('admin.shared.cancelChanges')}
          message={t('admin.shared.cancelChangesMessage')}
          onClose={() => setCancelConfirmOpen(false)}
          onConfirm={() => {
            setCancelConfirmOpen(false);
            nav('/admin/roles-permissions');
          }}
        />
      ) : null}
    </>
  );
}


